import React, { useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { IoChevronBack, IoChevronForward, IoOpenOutline } from 'react-icons/io5';
import Styles from './styles.module.css';
import { needsReview } from './scanMapping';
import type { ScanDoc } from './scanApi';
import type { ScanDraft } from './scanMapping';

// react-pdf 9.x は pdfjs-dist 4.x を同梱。ワーカーは CDN から読み込む
// （読み込めない場合もメインスレッドのフォールバックで表示は継続する）。
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

type Props = {
  scan: ScanDoc;
  saving: boolean;
  onSaveDraft: (draft: ScanDraft) => void;
  onSubmit: (draft: ScanDraft) => void;
  onBack: () => void;
};

// dot-path で入れ子オブジェクトを不変更新する
function setPath<T extends Record<string, any>>(obj: T, path: string, value: unknown): T {
  const keys = path.split('.');
  const clone: any = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] ?? {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}
function getPath(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce<any>((acc, k) => (acc == null ? acc : acc[k]), obj);
}

const Review: React.FC<Props> = ({ scan, saving, onSaveDraft, onSubmit, onBack }) => {
  const [draft, setDraft] = useState<ScanDraft>(scan.draft as ScanDraft);
  const [numPages, setNumPages] = useState<number>(scan.pageCount || 1);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const confidence = scan.confidence || {};
  const duplicates = (scan.duplicateCandidates || []).filter((d) => d.status === 'completed' || d.status === 'review');

  const update = (path: string, value: unknown) => setDraft((d) => setPath(d, path, value));

  const field = (
    label: string,
    path: string,
    opts: { textarea?: boolean; confKey?: string; placeholder?: string } = {},
  ) => {
    const flag = opts.confKey ? needsReview(confidence, opts.confKey) : false;
    const raw = getPath(draft, path);
    const value = raw == null ? '' : String(raw);
    return (
      <label className={Styles.field}>
        <span className={Styles.field_label}>
          {label}
          {flag && <span className={Styles.review_chip}>要確認</span>}
        </span>
        {opts.textarea ? (
          <textarea
            className={`${Styles.field_input} ${flag ? Styles.field_flagged : ''}`}
            value={value}
            placeholder={opts.placeholder}
            rows={3}
            onChange={(e) => update(path, e.target.value)}
          />
        ) : (
          <input
            className={`${Styles.field_input} ${flag ? Styles.field_flagged : ''}`}
            value={value}
            placeholder={opts.placeholder}
            onChange={(e) => update(path, e.target.value)}
          />
        )}
      </label>
    );
  };

  const insuranceText = useMemo(
    () => (Array.isArray(draft.employment20.insurance) ? draft.employment20.insurance.join('、') : ''),
    [draft.employment20.insurance],
  );

  return (
    <div className={Styles.review}>
      <div className={Styles.review_head}>
        <button className={Styles.link_btn} onClick={onBack}>一覧に戻る</button>
        <span className={Styles.review_filename}>{scan.originalFileName}</span>
        <a className={Styles.link_btn} href={scan.pdfUrl} target="_blank" rel="noopener noreferrer">
          <IoOpenOutline /> 元のPDFを開く
        </a>
      </div>

      {duplicates.length > 0 && (
        <div className={Styles.dup_banner}>
          似た求人票がすでに登録されています（{duplicates.map((d) => d.originalFileName || d.scanId).join('、')}）。内容をご確認ください。
        </div>
      )}

      <div className={Styles.review_body}>
        {/* 左：元PDF */}
        <div className={Styles.pdf_pane}>
          <Document
            file={scan.pdfUrl}
            onLoadSuccess={({ numPages: n }) => { setNumPages(n); setPageNumber(1); }}
            loading={<p className={Styles.pdf_loading}>PDFを読み込んでいます…</p>}
            error={<p className={Styles.pdf_loading}>PDFを表示できませんでした。「元のPDFを開く」からご確認ください。</p>}
          >
            <Page pageNumber={pageNumber} width={500} renderAnnotationLayer={false} renderTextLayer={false} />
          </Document>
          {numPages > 1 && (
            <div className={Styles.pdf_nav}>
              <button disabled={pageNumber <= 1} onClick={() => setPageNumber((p) => Math.max(1, p - 1))}>
                <IoChevronBack />
              </button>
              <span>{pageNumber} / {numPages}</span>
              <button disabled={pageNumber >= numPages} onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}>
                <IoChevronForward />
              </button>
            </div>
          )}
        </div>

        {/* 右：AIが読み取った求人情報 */}
        <div className={Styles.form_pane}>
          <p className={Styles.form_hint}>
            AIが読み取った内容です。「要確認」の項目を中心に確認・修正してください。
          </p>

          <h4 className={Styles.form_section}>基本情報</h4>
          {field('会社名', 'info.companyName', { confKey: 'info.companyName' })}
          {field('サロン名・表示名', 'summary.name', { confKey: 'summary.name' })}
          {field('事業所所在地', 'info.address', { textarea: true, confKey: 'info.address' })}
          {field('設立', 'info.establishedDate', { confKey: 'info.establishedDate' })}
          {field('従業員数', 'info.numberOfEmployees', { confKey: 'info.numberOfEmployees' })}

          <h4 className={Styles.form_section}>仕事内容・雇用形態</h4>
          {field('雇用形態', 'employment20.type', { confKey: 'employment20.type' })}
          {field('仕事内容', 'employment20.contents', { textarea: true, confKey: 'employment20.contents' })}

          <h4 className={Styles.form_section}>給与</h4>
          {field('基本給（円）', 'employment20.baseSalary', { confKey: 'salary' })}
          {field('月給（円）', 'employment20.totalSalary', { confKey: 'salary' })}
          {field('固定残業代（円）', 'employment20.fixedOvertime.price', { confKey: 'salary' })}
          {field('給与メモ（原文など）', 'employment20.otherText', { textarea: true, confKey: 'salary' })}

          <h4 className={Styles.form_section}>勤務時間・休日</h4>
          {field('始業', 'benefit.workingHours.time1Start', { confKey: 'benefit.workingHours', placeholder: '09:00' })}
          {field('終業', 'benefit.workingHours.time1End', { confKey: 'benefit.workingHours', placeholder: '18:00' })}
          {field('休憩（分）', 'benefit.dailyBreak', { confKey: 'benefit.dailyBreak' })}
          {field('休日制度', 'benefit.holidayPolicy.text', { confKey: 'benefit.holidayPolicy', placeholder: '完全週休2日制 など' })}
          {field('年間休日（日）', 'benefit.annualHolidays', { confKey: 'benefit.annualHolidays' })}
          {field('休日・休暇メモ', 'benefit.otherText', { textarea: true, confKey: 'benefit.otherText' })}

          <h4 className={Styles.form_section}>待遇</h4>
          <label className={Styles.field}>
            <span className={Styles.field_label}>
              社会保険
              {needsReview(confidence, 'employment20.insurance') && <span className={Styles.review_chip}>要確認</span>}
            </span>
            <input
              className={Styles.field_input}
              value={insuranceText}
              placeholder="健康保険、厚生年金、雇用保険、労災保険"
              onChange={(e) => {
                const list = e.target.value.split(/[、,／/・]/).map((x) => x.trim()).filter(Boolean);
                setDraft((d) => {
                  const next = setPath(d, 'employment20.insurance', list);
                  return setPath(next, 'benefit.insurance', list);
                });
              }}
            />
          </label>
          {field('福利厚生', 'employment20.benefits', { textarea: true, confKey: 'employment20.benefits' })}
          {field('各種手当', 'employment20.allowance', { textarea: true, confKey: 'employment20.allowance' })}
          {field('試用期間', 'employment20.employmentTrial.text', { confKey: 'employment20.employmentTrial' })}

          <h4 className={Styles.form_section}>応募・選考</h4>
          {field('募集人数', 'applicationInfo.recruitsBeauty', { confKey: 'applicationInfo.selection' })}
          {field('選考ステップ1', 'applicationInfo.selectionRound1', { confKey: 'applicationInfo.selection' })}
          {field('選考ステップ2', 'applicationInfo.selectionRound2', { confKey: 'applicationInfo.selection' })}
          {field('選考ステップ3', 'applicationInfo.selectionRound3', { confKey: 'applicationInfo.selection' })}
          {field('応募資格・選考メモ', 'applicationInfo.selectionOtherDetails', { textarea: true, confKey: 'applicationInfo.selectionOtherDetails' })}

          <h4 className={Styles.form_section}>連絡先</h4>
          {field('採用担当者名', 'info.recruiterName', { confKey: 'info.recruiterName' })}
          {field('電話番号', 'info.phoneNumber', { confKey: 'info.phoneNumber' })}
          {field('メールアドレス', 'info.email', { confKey: 'info.email' })}
          {field('ホームページ', 'info.homepageUrl', { confKey: 'info.homepageUrl' })}
          {field('Instagram', 'info.instagram')}

          <h4 className={Styles.form_section}>備考</h4>
          {field('企業PR・備考', 'summary.companyPr', { textarea: true })}

          <div className={Styles.review_actions}>
            <button className={Styles.btn_secondary} disabled={saving} onClick={() => onSaveDraft(draft)}>
              下書きを保存
            </button>
            <button className={Styles.btn_primary} disabled={saving} onClick={() => onSubmit(draft)}>
              {saving ? '保存中…' : '登録する'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Review;
